/**
 * lib/mailbox.ts
 * ------------------------------------------------------------------
 * WHAT CAME BACK.
 *
 * The portal has always been able to send. It has never been able to
 * hear. On 7 September it sent 905 fee reminders, Gmail accepted every
 * one, and 905 bounces landed in a mailbox no part of this system could
 * see — so the arrears screen reported a clean run while the school had
 * reached 57 families. The sending code cannot know this: a mail server
 * accepts a message and refuses the recipient minutes later, out of
 * band, to the sender's inbox.
 *
 * This module reads that inbox back into the app. Three kinds of thing
 * arrive and they are NOT the same:
 *
 *   A BOUNCE is a machine saying the message did not arrive. It is only
 *     useful if we can say WHICH message — and we can, because every
 *     reminder records its Message-ID and the bounce quotes it back.
 *     That turns "905 sends, 57 arrivals" into a named list of families
 *     nobody has actually chased.
 *
 *   A REPLY is a parent answering. It is the school's post, and the
 *     office must be able to read and answer it inside the portal —
 *     which is the whole reason the mailbox password never has to leave
 *     this system.
 *
 *   AN AUTO-REPLY is neither. "I am on leave until the 5th" is not an
 *     answer about school fees, and counting it as one tells a bursar a
 *     family has responded when nobody has read a word.
 *
 * PERMANENT AND TEMPORARY FAILURES ARE DIFFERENT FAILURES. A 5.x.x is
 * the address rejecting mail for good; a 4.x.x is a full mailbox or a
 * server having a bad afternoon, and it will very likely go through
 * tomorrow. Marking a family unreachable on a temporary failure stops
 * the school chasing somebody it can perfectly well reach — so only a
 * permanent failure condemns an address here.
 * ------------------------------------------------------------------ */

export const MAIL_COLLECTIONS = {
  /** Everything read back out of the mailbox. */
  inbox: 'mailInbox',
  /** One document per address a permanent bounce has condemned. */
  undeliverable: 'mailUndeliverable',
} as const;

export type MailKind = 'reply' | 'bounce' | 'auto' | 'other';

/** A message as the sync function hands it over — headers already parsed. */
export interface RawMessage {
  /** The mailbox's own id for it, so a re-sync does not duplicate. */
  uid: string;
  messageId?: string;
  inReplyTo?: string;
  /** Space- or comma-separated, oldest first, per RFC 5322. */
  references?: string;
  from?: string;
  fromName?: string;
  to?: string;
  subject?: string;
  date?: string;
  text?: string;
  html?: string;
  /** Lower-cased header names → value. Only the ones we read. */
  headers?: Record<string, string>;
}

/* ===================== who sent it ================================= */

/** `"Mrs T Ndlovu" <a@b.com>` → `a@b.com`. Lower-cased, trimmed. */
export function addressOf(v: string | undefined | null): string {
  const raw = String(v ?? '').trim();
  const angled = raw.match(/<([^>]+)>/);
  const addr = (angled ? angled[1] : raw).trim().toLowerCase();
  return /^[^@\s]+@[^@\s]+$/.test(addr) ? addr : '';
}

/** The display name, when there is one. */
export function displayNameOf(v: string | undefined | null): string {
  const raw = String(v ?? '').trim();
  const m = raw.match(/^\s*"?([^"<]*?)"?\s*</);
  return m ? m[1].trim() : '';
}

const DAEMONS = ['mailer-daemon', 'postmaster', 'mail-daemon'];

/* ===================== what kind of thing is it ==================== */

/**
 * Classify a message.
 *
 * ORDER MATTERS. A bounce is also technically automated, and an
 * out-of-office often quotes the original — so the machine-generated
 * report is identified first, the vacation notice second, and only what
 * survives both is treated as a person writing back.
 */
export function classify(m: RawMessage): MailKind {
  const h = m.headers ?? {};
  const from = addressOf(m.from);
  const local = from.split('@')[0] ?? '';
  const contentType = (h['content-type'] ?? '').toLowerCase();
  const subject = (m.subject ?? '').toLowerCase();

  /* The unambiguous, standards-defined case (RFC 3464). Gmail, Exchange
     and every serious MTA send this. */
  if (contentType.includes('report-type=delivery-status')) return 'bounce';
  if (DAEMONS.some((d) => local.startsWith(d))) return 'bounce';
  if ((h['x-failed-recipients'] ?? '').trim()) return 'bounce';
  if (/delivery status notification|undeliverable|delivery has failed|address not found/i
    .test(subject)) return 'bounce';

  /* An out-of-office. Auto-Submitted is the standard (RFC 3834); the
     X- headers are what Exchange and Gmail actually send. */
  const autoSubmitted = (h['auto-submitted'] ?? '').toLowerCase();
  if (autoSubmitted && autoSubmitted !== 'no') return 'auto';
  if (h['x-autoreply'] || h['x-autorespond'] || h['x-auto-response-suppress']) return 'auto';
  if ((h['precedence'] ?? '').toLowerCase() === 'auto_reply') return 'auto';
  if (/^(out of (the )?office|automatic reply|auto(matic)?[- ]?response)\b/i
    .test((m.subject ?? '').trim())) return 'auto';

  if (from) return 'reply';
  return 'other';
}

/**
 * A REPLY IS SOMEBODY WE CAN PLACE.
 *
 * The first real run collected 92 "replies": 31 Google notifications, 19
 * Yahoo DMARC reports, 11 from Microsoft, marketing from four more, and
 * about five actual parents. A bursar who opens Replies (92), wades
 * through newsletters and finds nothing stops opening it — and the one
 * parent asking about their child's fees is lost in the pile.
 *
 * So a message only counts as a reply when it can be tied to a pupil,
 * either by the thread it answers or by the address it came from.
 * Everything else is still collected and still listed under everything —
 * it is just not presented as a family writing to the school.
 *
 * A GENUINE PARENT ON AN UNKNOWN ADDRESS therefore lands in 'other', not
 * in the bin. That is the right way round: the office still sees it, it
 * simply is not counted as a reply we can act on.
 */
export function resolveKind(kind: MailKind, matchedToPupil: boolean): MailKind {
  if (kind === 'reply' && !matchedToPupil) return 'other';
  return kind;
}

/* ===================== whose post is it ============================ */

/**
 * WHICH DESK THIS BELONGS ON.
 *
 * The school already sends from five addresses — bursar@, admissions@,
 * hr@, academics@, info@ — and a reply goes back to the one it came
 * from. So the department is not something to infer from the words in a
 * message; it is written on the envelope. Reading it there is right far
 * more often than reading the prose, and when it is wrong a person can
 * move it.
 */
export const DEPARTMENTS = ['finance', 'admissions', 'hr', 'academics', 'office'] as const;
export type Department = (typeof DEPARTMENTS)[number];

export const DEPARTMENT_LABEL: Record<Department, string> = {
  finance: 'Bursary',
  admissions: 'Admissions',
  hr: 'HR & payroll',
  academics: 'Academics',
  office: 'Front office',
};

/** Alias local-part → desk. Extra names are cheap and a wrong guess is
 *  not, so anything unrecognised falls to the front office rather than
 *  being pushed at a department that did not send it. */
const ALIAS_DEPARTMENT: Record<string, Department> = {
  bursar: 'finance', bursary: 'finance', fees: 'finance', accounts: 'finance', finance: 'finance',
  admissions: 'admissions', apply: 'admissions', applications: 'admissions',
  hr: 'hr', payroll: 'hr', recruitment: 'hr',
  academics: 'academics', reports: 'academics', exams: 'academics',
  info: 'office', admin: 'office', office: 'office',
};

/**
 * Read the desk off the addresses a message was delivered to.
 *
 * `hint` is for a bounce, whose envelope names the mailbox rather than
 * the alias: a delivery report for a fee reminder is the bursary's
 * business even though it arrived addressed to admin@.
 */
export function departmentFor(
  addresses: (string | undefined | null)[],
  hint?: Department,
): Department {
  for (const a of addresses) {
    for (const part of String(a ?? '').split(',')) {
      const local = addressOf(part).split('@')[0] ?? '';
      const dept = ALIAS_DEPARTMENT[local];
      if (dept) return dept;
    }
  }
  return hint ?? 'office';
}

/**
 * The desk a message is ON, which is not always the desk it arrived at.
 *
 * A person who moves a misfiled message must not have it moved back by
 * the next sync, so the manual choice is stored separately and always
 * wins. The alias is a good guess; a colleague reading the thing is
 * better.
 */
export function deskOf(m: { department?: Department; departmentOverride?: Department }): Department {
  return m.departmentOverride ?? m.department ?? 'office';
}

/* ===================== reading a bounce ============================ */

export interface BounceDetail {
  /** The address that failed, when the report names one. */
  recipient: string;
  /** e.g. '5.1.1'. Empty when the report does not carry one. */
  status: string;
  /** True only for 5.x.x — see the header. */
  permanent: boolean;
  /** The server's own words, trimmed to something a bursar can read. */
  diagnostic: string;
}

const STATUS_RE = /^\s*Status:\s*([245]\.\d{1,3}\.\d{1,3})/im;
const FINAL_RE = /^\s*(?:Final|Original)-Recipient:\s*(?:rfc822;)?\s*(\S+)/im;
const DIAG_RE = /^\s*Diagnostic-Code:\s*(?:smtp;)?\s*([\s\S]*?)(?:\r?\n(?!\s)|$)/im;

/**
 * Pull the machine-readable part out of a delivery report.
 *
 * Falls back to the human paragraph, because Gmail's own bounces put the
 * useful sentence — "the domain parent.lkc.ac.bw could not be found" —
 * in the text part, and that sentence is the one that told us 577
 * addresses were manufactured by an import.
 */
export function readBounce(m: RawMessage): BounceDetail {
  const body = `${m.text ?? ''}\n${m.headers?.['x-failed-recipients'] ?? ''}`;

  const status = (body.match(STATUS_RE)?.[1] ?? '').trim();
  const failed = m.headers?.['x-failed-recipients'];
  const recipient = addressOf(failed) || addressOf(body.match(FINAL_RE)?.[1]) || '';

  let diagnostic = (body.match(DIAG_RE)?.[1] ?? '').replace(/\s+/g, ' ').trim();
  if (!diagnostic) {
    const line = (m.text ?? '')
      .split(/\r?\n/)
      .find((l) => /couldn't be found|could not be found|does not exist|doesn't exist|not found|rejected|blocked|quota|full/i.test(l));
    diagnostic = (line ?? '').replace(/\s+/g, ' ').trim();
  }

  return {
    recipient,
    status,
    /* NO STATUS MEANS NO CLAIM. An unparsed report is still a bounce and
       still worth showing, but condemning an address on a guess would
       stop the school chasing a family it can reach. */
    permanent: status.startsWith('5.'),
    diagnostic: diagnostic.slice(0, 300),
  };
}

/* ===================== tying it back to a send ===================== */

/** `<abc@mail>` → `abc@mail`; a References list → the ids in it. */
export function referenceIds(m: RawMessage): string[] {
  const raw = `${m.inReplyTo ?? ''} ${m.references ?? ''}`;
  return [...new Set(
    (raw.match(/<[^>\s]+>/g) ?? [])
      .map((s) => s.slice(1, -1).trim())
      .filter(Boolean),
  )];
}

/** A send we might be able to blame. */
export interface SentRef {
  messageId?: string;
  studentId?: string;
  studentName?: string;
  invoiceNo?: string;
  to?: string;
}

/**
 * Which send is this about?
 *
 * BY MESSAGE-ID FIRST, because it is the only certain link: the bounce
 * quotes the exact message it failed to deliver. Falling back to the
 * address is a guess that is usually right, and it is marked as a guess
 * so nothing downstream treats the two as equally sure.
 */
export function matchToSend(
  m: RawMessage,
  sends: SentRef[],
  bounce?: BounceDetail,
): { send: SentRef | null; how: 'messageId' | 'address' | 'none' } {
  const ids = new Set(referenceIds(m));
  if (ids.size) {
    const byId = sends.find((s) => s.messageId && ids.has(String(s.messageId).replace(/^<|>$/g, '')));
    if (byId) return { send: byId, how: 'messageId' };
  }

  const addr = bounce?.recipient || addressOf(m.from);
  if (addr) {
    const byAddr = sends.find((s) => addressOf(s.to) === addr);
    if (byAddr) return { send: byAddr, how: 'address' };
  }

  return { send: null, how: 'none' };
}

/* ===================== answering it ================================ */

/** 'Re: Re: Fees' → 'Re: Fees'. One prefix, however many arrive. */
export function replySubject(subject: string | undefined): string {
  const bare = String(subject ?? '').replace(/^(\s*re\s*:\s*)+/i, '').trim();
  return bare ? `Re: ${bare}` : 'Re:';
}

export interface ReplyHeaders {
  to: string;
  subject: string;
  inReplyTo?: string;
  references?: string;
}

/**
 * The headers that make a reply land in the same conversation.
 *
 * Without In-Reply-To and References the answer arrives as a NEW thread,
 * detached from the question. A parent who asked about one child's fees
 * gets an unheaded message about fees, and the office loses the thread
 * on their side too.
 */
export function replyHeaders(m: RawMessage): ReplyHeaders {
  const to = addressOf(m.from);
  const refs = [...referenceIds(m)];
  if (m.messageId) {
    const id = String(m.messageId).replace(/^<|>$/g, '');
    if (!refs.includes(id)) refs.push(id);
  }
  return {
    to,
    subject: replySubject(m.subject),
    ...(m.messageId ? { inReplyTo: `<${String(m.messageId).replace(/^<|>$/g, '')}>` } : {}),
    ...(refs.length ? { references: refs.map((r) => `<${r}>`).join(' ') } : {}),
  };
}

/* ===================== the stored record =========================== */

export interface InboxMessage {
  id: string;
  uid: string;
  kind: MailKind;
  from: string;
  fromName?: string;
  subject: string;
  receivedAt: string;
  /** Plain text, trimmed for storage — the full body is in the mailbox. */
  body: string;
  messageId?: string;
  /** Set on a bounce. */
  bounce?: BounceDetail;
  /** What this is about, when we could work it out. */
  studentId?: string;
  studentName?: string;
  invoiceNo?: string;
  matchedBy?: 'messageId' | 'address' | 'none';
  /** Read off the alias it was delivered to. */
  department?: Department;
  /** Set when somebody moved it. Always wins — see deskOf. */
  departmentOverride?: Department;
  departmentMovedBy?: string;
  departmentMovedAt?: string;
  /** Office workflow. */
  handled?: boolean;
  handledBy?: string;
  handledAt?: string;
  repliedAt?: string;
}

/** The line the office reads first. Says what happened, not what we hope. */
export function summaryLine(m: InboxMessage): string {
  if (m.kind === 'bounce') {
    const who = m.studentName ? ` for ${m.studentName}` : '';
    if (m.bounce?.permanent) {
      return `Did not arrive${who} — ${m.bounce.recipient || 'the address'} rejects mail permanently.`;
    }
    if (m.bounce?.status) {
      return `Did not arrive${who} — a temporary failure (${m.bounce.status}). It may go through later.`;
    }
    return `Did not arrive${who} — the reason was not given in a form we can read.`;
  }
  if (m.kind === 'auto') return 'Automatic reply — nobody has read this yet.';
  if (m.kind === 'reply') {
    return m.studentName ? `Reply about ${m.studentName}.` : 'Reply.';
  }
  return 'Message.';
}

/**
 * Should this bounce condemn the address?
 *
 * Only a permanent failure, and only when the report actually named the
 * address that failed. Everything else stays chaseable.
 */
export function condemns(b: BounceDetail | undefined): boolean {
  return Boolean(b && b.permanent && b.recipient);
}
